import { useEffect, useCallback } from 'react';

// Performance monitoring hook
export const usePerformance = () => {
  const measurePageLoad = useCallback(() => {
    if (typeof window !== 'undefined' && 'performance' in window) {
      const navigation = performance.getEntriesByType('navigation')[0];
      if (navigation) {
        // Performance metrics measured but not logged in production
      }
    }
  }, []);

  const measureComponentRender = useCallback((componentName, startTime) => {
    const endTime = performance.now();
    const renderTime = endTime - startTime;
    return renderTime;
  }, []);

  const trackUserInteraction = useCallback((action, element) => {
    if (typeof window !== 'undefined' && 'performance' in window) {
      performance.mark(`${action}-${element}-start`);
      
      // Track the interaction after a small delay to capture the full interaction
      setTimeout(() => {
        performance.mark(`${action}-${element}-end`);
        performance.measure(
          `${action}-${element}`,
          `${action}-${element}-start`,
          `${action}-${element}-end`
        );
        
        const measure = performance.getEntriesByName(`${action}-${element}`)[0];
        if (measure) {
          // Interaction duration measured
        }
      }, 100);
    }
  }, []);

  const preloadResource = useCallback((url, type = 'fetch') => {
    if (typeof window !== 'undefined') {
      const link = document.createElement('link');
      link.rel = 'preload';
      link.href = url;
      
      switch (type) {
        case 'image':
          link.as = 'image';
          break;
        case 'script':
          link.as = 'script';
          break;
        case 'style':
          link.as = 'style';
          break;
        default:
          link.as = 'fetch';
          link.crossOrigin = 'anonymous';
      }
      
      document.head.appendChild(link);
    }
  }, []);

  const prefetchPage = useCallback((url) => {
    if (typeof window !== 'undefined') {
      const link = document.createElement('link');
      link.rel = 'prefetch';
      link.href = url;
      document.head.appendChild(link);
    }
  }, []);

  useEffect(() => {
    // Measure initial page load
    if (document.readyState === 'complete') {
      measurePageLoad();
    } else {
      window.addEventListener('load', measurePageLoad);
      return () => window.removeEventListener('load', measurePageLoad);
    }
  }, [measurePageLoad]);

  return {
    measureComponentRender,
    trackUserInteraction,
    preloadResource,
    prefetchPage,
    measurePageLoad
  };
};

// Image optimization utilities
export const useImageOptimization = () => {
  const getOptimizedImageUrl = useCallback((originalUrl, options = {}) => {
    if (!originalUrl) return null;
    
    const {
      width,
      height,
      quality = 80,
      format = 'webp'
    } = options;
    
    // If it's already an external URL, return as is
    if (originalUrl.startsWith('http') && !originalUrl.includes('localhost')) {
      return originalUrl;
    }
    
    // For local images, add optimization parameters
    const baseUrl = originalUrl.startsWith('/') ? 
      `/api${originalUrl}` : 
      originalUrl;
    
    try {
      const url = new URL(baseUrl);
      if (width) url.searchParams.set('w', width);
      if (height) url.searchParams.set('h', height);
      url.searchParams.set('q', quality);
      url.searchParams.set('f', format);
      
      return url.toString();
    } catch {
      return originalUrl;
    }
  }, []);

  const preloadImage = useCallback((src) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = reject;
      img.src = src;
    });
  }, []);

  const lazyLoadImage = useCallback((imgElement, src, options = {}) => {
    const { threshold = 0.1, rootMargin = '50px' } = options;
    
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const img = entry.target;
            img.src = src;
            img.classList.remove('lazy');
            observer.unobserve(img);
          }
        });
      },
      { threshold, rootMargin }
    );

    if (imgElement) {
      observer.observe(imgElement);
    }

    return () => {
      if (imgElement) {
        observer.unobserve(imgElement);
      }
    };
  }, []);

  return {
    getOptimizedImageUrl,
    preloadImage,
    lazyLoadImage
  };
};

// Bundle size optimization
export const useBundleOptimization = () => {
  const loadComponentAsync = useCallback((importFunction) => {
    const React = require('react');
    return React.lazy(() => 
      importFunction().catch(err => {
        console.error('Failed to load component:', err);
        // Return a fallback component
        return { default: () => React.createElement('div', null, 'שגיאה בטעינת הקומפוננט') };
      })
    );
  }, []);

  return {
    loadComponentAsync
  };
};
